import { AnchorRiskResponseSchema, type AnchorRiskResponse } from "@exploitlens/ui-contracts";

import { isReviewedAnchorWallet } from "./cases";

export const ANCHOR_RISK_ENDPOINT = "https://anchor-miner.vercel.app/api/risk-check";

const REVIEWED_WALLET_PATTERN = /^0x[0-9a-fA-F]{40}$/;

/**
 * Reads Anchor's public production endpoint exactly once and validates the
 * complete response before returning it. Callers decide how to display an
 * unavailable state; this client never substitutes or remaps a verdict.
 */
export async function fetchAnchorRisk(
  wallet: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AnchorRiskResponse> {
  if (!REVIEWED_WALLET_PATTERN.test(wallet)) {
    throw new Error("Anchor wallet must be a 20-byte hex address");
  }
  if (!isReviewedAnchorWallet(wallet)) {
    throw new Error("Anchor wallet is not approved for live queries");
  }

  const url = new URL(ANCHOR_RISK_ENDPOINT);
  url.searchParams.set("wallet", wallet);

  let response: Response;
  try {
    response = await fetchImpl(url.toString(), {
      headers: { Accept: "application/json" },
    });
  } catch (cause) {
    throw new Error(
      `Anchor unavailable: network error${cause instanceof Error ? `: ${cause.message}` : ""}`,
    );
  }

  if (!response.ok) {
    throw new Error(`Anchor unavailable: HTTP ${response.status}`);
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new Error("Anchor response was not valid JSON");
  }

  const parsed = AnchorRiskResponseSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error("Anchor response failed schema validation");
  }

  return parsed.data;
}
